import { MouseEvent } from 'react';
import { useIntl } from 'react-intl';

import { type TimelinePreview } from '@/shared/ui';

import { ensureRefreshImageSrc } from '@/entities/assets';

import {
  getReportPreviewTestIDSegment,
  StructuredReportsAndStudiesType,
} from '@/features/reportCards';

import { sanitizeForTestID } from '../lib';

import { useReportCard } from './useReportCard';
import { useReportCardActions } from './useReportCardActions';
import { useReportTimelineMeta } from './useReportTimelineMeta';

type UseReportTimelineItemOptions = {
  showSource?: boolean;
};

export const useReportTimelineItem = (
  structuredReportsAndStudies: StructuredReportsAndStudiesType,
  { showSource }: UseReportTimelineItemOptions = {},
) => {
  const { formatDate } = useIntl();
  const reportCard = useReportCard(structuredReportsAndStudies);
  const {
    isReportGenerationCompleted,
    isIOXRayOrPanowings,
    reportType,
    date,
    report,
    isReportFailed,
    isReportSigned,
    isReportOrderAvailable,
    reorderFunding,
    isLoadingPDF,
    cardPreviewSrc,
    isIntraoralScanReport,
    isRegenerateAvailable,
    shouldHideDownload,
    reportTitle,
    relevantStudy,
    reportLink,
    handleCardClick,
    handleCardAuxClick,
    reorderReport,
    downloadReportHandler,
  } = reportCard;

  const {
    isRemoving,
    isReportHidden,
    enableArchiveReportsFeature,
    CanArchive,
    CanDelete,
    CanRestore,
    handleRemoveWithAnimation,
    handleHideWithAnimation,
    handleRestoreWithAnimation,
    openReportInfoModal,
  } = useReportCardActions({ structuredReportsAndStudies, reportCard });

  const meta = useReportTimelineMeta({ date, relevantStudy, showSource });

  if (!report) {
    return null;
  }

  const isCompleted = Boolean(isReportGenerationCompleted);
  const previewTestIDSegment = getReportPreviewTestIDSegment(reportType);
  const preview: TimelinePreview | undefined =
    isCompleted && cardPreviewSrc
      ? {
          src: cardPreviewSrc,
          alt: reportTitle,
          contain: isIOXRayOrPanowings,
          light: isIntraoralScanReport,
          testID: previewTestIDSegment
            ? `preview-report-${sanitizeForTestID(previewTestIDSegment)}-${report.ID}`
            : undefined,
          onError: ensureRefreshImageSrc,
        }
      : undefined;

  // Timeline items are not real links, so a middle click has to open the
  // report itself; the card hook only tracks the click.
  const handleAuxClick = (event: MouseEvent<HTMLElement>) => {
    handleCardAuxClick(event);

    const url = reportLink?.href ?? reportLink?.to;

    if (event.button === 1 && url) {
      event.stopPropagation();
      window.open(url, '_blank', 'noopener,noreferrer');
    }
  };

  return {
    report,
    reportType,
    isCompleted,
    isHidden: isReportHidden,
    isRemoving,
    title: reportTitle,
    time: date ? formatDate(date, { timeStyle: 'short' }) : undefined,
    meta,
    preview,
    detailsPopoverProps: {
      reportID: report.ID,
      sourceStudyIDs: report.SourceStudyIDs,
      studies: structuredReportsAndStudies.studies ?? [],
      canArchive: CanArchive,
      canDelete: CanDelete,
      canRestore: CanRestore,
      hideMode: enableArchiveReportsFeature,
      isHidden: isReportHidden,
      isReportGenerationCompleted: isCompleted,
      isLoadingPDF,
      shouldHideDownload,
      onDetailsView: openReportInfoModal,
      onRemoveReport: handleRemoveWithAnimation,
      onDownloadReport: downloadReportHandler,
      onHideReport: handleHideWithAnimation,
      onRestoreReport: handleRestoreWithAnimation,
    },
    footerProps: {
      isReportFailed: Boolean(isReportFailed),
      isReportSigned: Boolean(isReportSigned),
      reorderFunding,
      canReorder: Boolean(isRegenerateAvailable),
      isReorderDisabled:
        isReportOrderAvailable && reorderFunding.source !== 'diagnocoins',
      onReorder: reorderReport,
    },
    handleCardClick,
    handleAuxClick,
  };
};
